import { Alert, Button, Loader, Text, Tooltip } from "@mantine/core";
import { useTRPC } from "../../../../trpc";
import { useCallback, useMemo } from "react";
import { FullPageLoader } from "../../../../components/FullPageLoader";
import { FormattedMessage, useIntl } from "react-intl";
import { useMutation, useQuery } from "@tanstack/react-query";
import { AgGridTable } from "../../../../components/AgGridTable";
import { IconDownload } from "@tabler/icons-react";
import { useMapkaNotification } from "../../../../hooks/notifications";
import type { ColDef, ICellRendererParams } from "ag-grid-community";
import type { RouterOutputs } from "@project/api";

type Invoice = RouterOutputs["getInvoices"][number];

const formatCurrency = (amount: number, currency: string) => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency.toUpperCase(),
  }).format(amount / 100);
};

const formatDate = (timestamp: number) => {
  return new Date(timestamp * 1000).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const getStatusColor = (status: string | null) => {
  switch (status) {
    case "paid":
      return "green";
    case "open":
      return "blue";
    case "draft":
      return "gray";
    case "void":
      return "gray";
    case "uncollectible":
      return "red";
    case null:
    default:
      return "gray";
  }
};

export const InvoicesTable = () => {
  const intl = useIntl();
  const trpc = useTRPC();
  const mapkaNotification = useMapkaNotification();

  const { data: invoices = [], isLoading: isLoadingInvoices } = useQuery(trpc.getInvoices.queryOptions());
  const { mutate: downloadInvoice, isPending: isDownloading } = useMutation(trpc.downloadInvoice.mutationOptions());

  const handleDownloadInvoice = useCallback((stripeInvoiceId: string) => {
    downloadInvoice({ stripeInvoiceId }, {
      onSuccess: ({ buffer, name }) => {
        const url = window.URL.createObjectURL(
          new Blob([new Uint8Array(buffer.data)], { type: "application/pdf" }),
        );
        const link = document.createElement("a");
        link.href = url;
        link.download = name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      },
      onError: () => {
        mapkaNotification({
          title: intl.formatMessage({
            defaultMessage: "Download failed",
            id: "notification.downloadFailed",
          }),
          message: intl.formatMessage({
            defaultMessage: "Failed to download invoice. Please try again.",
            id: "notification.downloadFailedMessage",
          }),
          variant: "error",
        });
      },
    });
  }, [downloadInvoice, intl, mapkaNotification]);

  const DownloadCellRenderer = ({ data: rowData }: ICellRendererParams<Invoice>) => {
    if (!rowData) return null;
    return (
      <Tooltip label={intl.formatMessage({ defaultMessage: "Download invoice", id: "tooltip.downloadInvoice" })}>
        <Button
          variant="default"
          size="xs"
          onClick={() => { handleDownloadInvoice(rowData.id); }}
          disabled={isDownloading}
          leftSection={isDownloading ? <Loader size="xs" /> : <IconDownload size={16} />}
        >
          Pdf
        </Button>
      </Tooltip>
    );
  };

  const columnDefs: ColDef<Invoice>[] = useMemo(() => [
    {
      field: "name",
      headerName: intl.formatMessage({ defaultMessage: "Invoice #", id: "column.invoiceName" }),
      sortable: true,
    },
    {
      field: "status",
      headerName: intl.formatMessage({ defaultMessage: "Status", id: "column.status" }),
      cellRenderer: ({ value }: { value: string | null }) => (
        <Text size="sm" c={getStatusColor(value)} tt="capitalize">
          {value ?? "-"}
        </Text>
      ),
    },
    {
      field: "created",
      headerName: intl.formatMessage({ defaultMessage: "Date", id: "column.date" }),
      sortable: true,
      valueFormatter: ({ value }) => formatDate(value as number),
      sort: "desc",
    },

    {
      field: "amountDue",
      headerName: intl.formatMessage({ defaultMessage: "Amount", id: "column.amount" }),
      valueFormatter: ({ data }) => data ? formatCurrency(data.amountDue, data.currency) : "-",
    },
    {
      headerName: intl.formatMessage({ defaultMessage: "Download", id: "column.download" }),
      cellRenderer: DownloadCellRenderer,
      sortable: false,
      filter: false,
      maxWidth: 100,
    },
  ], [intl, handleDownloadInvoice, isDownloading]);

  if (isLoadingInvoices) {
    return <FullPageLoader />;
  }

  if (invoices.length === 0) {
    return (
      <Alert
        variant="light"
        title={intl.formatMessage({ defaultMessage: "No invoices", id: "title.noInvoices" })}
      >
        <FormattedMessage
          defaultMessage="You don't have any invoices yet."
          id="text.noInvoicesDescription"
        />
      </Alert>
    );
  }

  return (
    <AgGridTable
      rowData={invoices}
      columnDefs={columnDefs}
      domLayout="autoHeight"
      pagination={true}
      paginationPageSize={20}
      autoSizeStrategy={{
        type: "fitGridWidth",
        defaultMinWidth: 100,
      }}
      suppressHorizontalScroll={false}
      defaultColDef={{
        sortable: true,
        filter: true,
        resizable: true,
      }}
    />
  );
};
